import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash, Check, EditPencil, Xmark, Heart, ShopFourTiles, Clock, Box } from "iconoir-react";
import {
    fetchShopping,
    addShopping,
    updateShopping,
    deleteShopping,
    fetchStores,
    addStore,
    firstName,
    fetchHousehold,
    bulkCheckShopping,
} from "@/lib/api";
import { avatarColor, initials } from "@/pages/MiloApp";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

const SORT_IDS = ["category", "newest", "oldest", "store", "alpha", "user"];
const BOUGHT_FILTER_IDS = ["this_week", "last_week", "overall", "avg_per_week"];
const CATEGORIES = ["Produce", "Dairy", "Meat", "Pantry", "Frozen", "Bakery", "Beverages", "Other"];
// "groceries" is a VIRTUAL tag — items default to groceries when home_improvement=false.
// It's mutually exclusive with "home" in the SHOW filter row, but tags like personal/later/priority
// remain orthogonal.
const TAG_IDS = ["groceries", "personal", "later", "priority", "home"];
// Map UI tag id -> ShoppingItem boolean field (groceries has no field — derived from !home_improvement)
const TAG_FIELD = { personal: "personal", later: "tagged_later", priority: "priority", home: "home_improvement" };

function startOfWeek(d = new Date()) {
    const x = new Date(d);
    const day = (x.getDay() + 6) % 7;
    x.setHours(0, 0, 0, 0);
    x.setDate(x.getDate() - day);
    return x;
}

export default function ShoppingTab({ session, members, onRefresh, onGoto }) {
    const { t } = useT();
    const SORTS = SORT_IDS.map((id) => ({ id, label: t(`shop.sort.${id}`) }));
    const BOUGHT_FILTERS = BOUGHT_FILTER_IDS.map((id) => ({ id, label: t(`shop.bought_filter.${id}`) }));
    const [items, setItems] = useState([]);
    const [stores, setStores] = useState([]);
    const [name, setName] = useState("");
    const [expanded, setExpanded] = useState(false);
    const [personal, setPersonal] = useState(false);
    const [store, setStore] = useState("");
    const [category, setCategory] = useState("");
    const [sort, setSort] = useState("category");
    const [show, setShow] = useState({ groceries: true, personal: false, later: false, priority: false, home: false });
    const [boughtFilter, setBoughtFilter] = useState("this_week");
    const [editingId, setEditingId] = useState(null);
    const [showAddStore, setShowAddStore] = useState(false);
    const [newStoreName, setNewStoreName] = useState("");
    const [fixDismissed, setFixDismissed] = useState(false);
    const [fixBusy, setFixBusy] = useState(false);
    const [autoCheck, setAutoCheck] = useState(false);

    const load = async () => {
        const [its, sts, hh] = await Promise.all([
            fetchShopping(session.household_id),
            fetchStores(session.household_id),
            fetchHousehold(session.household_id).catch(() => null),
        ]);
        setItems(its);
        setStores(sts);
        setAutoCheck(!!hh?.household?.menu_settings?.auto_check_in_stock);
    };
    useEffect(() => {
        load();
    }, [session.household_id]);

    const memberOf = (mid) => members?.find((m) => m.id === mid);

    const add = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        await addShopping(session.household_id, {
            name: name.trim(),
            personal,
            store: store || null,
            category: category || null,
            member_id: session.member_id,
        });
        setName("");
        setStore("");
        setCategory("");
        setPersonal(false);
        setExpanded(false);
        await load();
        onRefresh?.();
    };

    const toggle = async (it) => {
        try {
            await updateShopping(session.household_id, it.id, { checked: !it.checked });
        } catch (e) {
            toast.error(t("shop.couldnt_update_item"));
            return;
        }
        await load();
        onRefresh?.();
    };

    const remove = async (it) => {
        await deleteShopping(session.household_id, it.id);
        await load();
        onRefresh?.();
        toast.success(t("shop.removed"));
    };

    const tagLater = async (it) => {
        await updateShopping(session.household_id, it.id, { tagged_later: !it.tagged_later });
        await load();
        toast.success(it.tagged_later ? t("shop.untagged") : t("shop.tagged_later"));
    };

    const saveEdit = async (id, updates) => {
        await updateShopping(session.household_id, id, updates);
        setEditingId(null);
        await load();
        toast.success(t("shop.updated"));
    };

    const submitNewStore = async (e) => {
        e.preventDefault();
        if (!newStoreName.trim()) return;
        try {
            await addStore(session.household_id, newStoreName.trim());
            const sts = await fetchStores(session.household_id);
            setStores(sts);
            setStore(newStoreName.trim());
            setNewStoreName("");
            setShowAddStore(false);
            toast.success(t("shop.store_added"));
        } catch {
            toast.error(t("shop.couldnt_add_store"));
        }
    };

    // Classic todo behavior: checked items disappear from "Missing items"
    // and only show in the "Bought" section below.
    const pending = useMemo(() => {
        const cmp = {
            newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
            oldest: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
            alpha: (a, b) => (a.name || "").localeCompare(b.name || ""),
            store: (a, b) => (a.store || "zzz").localeCompare(b.store || "zzz") || (a.created_at || "").localeCompare(b.created_at || ""),
            category: (a, b) => (a.category || "zzz").localeCompare(b.category || "zzz") || (a.name || "").localeCompare(b.name || ""),
            user: (a, b) => {
                const an = memberOf(a.created_by_member_id)?.name || "zzz";
                const bn = memberOf(b.created_by_member_id)?.name || "zzz";
                return an.localeCompare(bn) || (a.created_at || "").localeCompare(b.created_at || "");
            },
        };
        return items
            .filter((i) => !i.checked)
            // Show filter: 
            //   - groceries (virtual): items WITHOUT home_improvement hidden when OFF
            //   - home: items WITH home_improvement hidden when OFF
            //   - personal/later/priority: items WITH that tag hidden when OFF
            //   - Untagged groceries-default items hidden iff !show.groceries
            .filter((i) => {
                const isHome = !!i.home_improvement;
                const isGrocery = !isHome;
                if (isGrocery && !show.groceries) return false;
                if (isHome && !show.home) return false;
                for (const tagId of ["personal", "later", "priority"]) {
                    if (!show[tagId] && i[TAG_FIELD[tagId]]) return false;
                }
                return true;
            })
            .sort(cmp[sort] || cmp.newest);
    }, [items, sort, show]);

    const done = useMemo(() => items.filter((i) => i.checked), [items]);

    // Pending items currently visible in the missing list that also exist in the pantry.
    const inPantryPending = useMemo(
        () => pending.filter((i) => i.in_pantry),
        [pending]
    );

    const onFixList = async () => {
        if (!inPantryPending.length) {
            toast.message(t("shop.fix_no_matches"));
            return;
        }
        // Auto-check mode: silent bulk-check, no banner
        if (autoCheck) {
            await runBulkCheck();
            return;
        }
        // Hybrid mode: ensure banner is shown (re-enable if dismissed) — clicking the
        // button is also the place to "refresh" the banner state.
        setFixDismissed(false);
        toast.message(
            inPantryPending.length === 1
                ? t("shop.fix_banner_one")
                : t("shop.fix_banner_other", { n: inPantryPending.length })
        );
    };

    const runBulkCheck = async () => {
        const ids = inPantryPending.map((i) => i.id);
        if (!ids.length) return;
        setFixBusy(true);
        // Optimistic update
        setItems((list) => list.map((i) => (ids.includes(i.id) ? { ...i, checked: true, completed_at: new Date().toISOString() } : i)));
        try {
            const res = await bulkCheckShopping(session.household_id, ids);
            const n = res.checked || ids.length;
            toast.success(
                n === 1
                    ? t("shop.fix_checked_toast_one")
                    : t("shop.fix_checked_toast_other", { n })
            );
            setFixDismissed(true);
        } catch (e) {
            // Roll back on failure
            await load();
            toast.error(e?.response?.data?.detail || "Failed");
        } finally {
            setFixBusy(false);
        }
    };

    const boughtStats = useMemo(() => {
        const now = new Date();
        const thisWk = startOfWeek(now);
        const lastWk = new Date(thisWk); lastWk.setDate(lastWk.getDate() - 7);
        const cutoffThis = thisWk.toISOString();
        const cutoffLast = lastWk.toISOString();
        const completed = done.map((d) => d.completed_at || d.created_at).filter(Boolean);
        const inThis = completed.filter((c) => c >= cutoffThis).length;
        const inLast = completed.filter((c) => c >= cutoffLast && c < cutoffThis).length;
        const overall = completed.length;
        // Avg/week: from oldest completion to now
        let avg = 0;
        if (completed.length > 0) {
            const oldest = new Date(completed.reduce((a, b) => (a < b ? a : b)));
            const weeks = Math.max(1, Math.ceil((now - oldest) / (7 * 86400000)));
            avg = +(overall / weeks).toFixed(1);
        }
        return { this_week: inThis, last_week: inLast, overall, avg_per_week: avg };
    }, [done]);

    const boughtList = useMemo(() => {
        const now = new Date();
        const thisWk = startOfWeek(now).toISOString();
        const lastWkStart = new Date(startOfWeek(now)); lastWkStart.setDate(lastWkStart.getDate() - 7);
        const lastWkStartIso = lastWkStart.toISOString();
        if (boughtFilter === "this_week") return done.filter((d) => (d.completed_at || d.created_at) >= thisWk);
        if (boughtFilter === "last_week") return done.filter((d) => {
            const c = d.completed_at || d.created_at;
            return c >= lastWkStartIso && c < thisWk;
        });
        return done; // overall + avg show full list
    }, [done, boughtFilter]);

    return (
        <div data-testid="shopping-tab" className="space-y-5">
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs font-semibold text-[#5b6d63] uppercase tracking-widest">{t("shop.eyebrow")}</div>
                    <div className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight mt-1">{t("shop.title")}</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={onFixList}
                        disabled={fixBusy}
                        className="brut-btn brut-btn-secondary text-xs sm:text-sm whitespace-nowrap"
                        data-testid="fix-shopping-btn"
                    >
                        {fixBusy ? t("shop.fix_btn_working") : t("shop.fix_btn")}
                    </button>
                    {onGoto && (
                        <button
                            type="button"
                            data-testid="open-inventory-btn"
                            onClick={() => onGoto("inventory")}
                            className="brut-btn brut-btn-secondary text-xs sm:text-sm whitespace-nowrap shrink-0"
                        >
                            <Box className="w-4 h-4" /> {t("inventory.shop_button")}
                        </button>
                    )}
                </div>
            </div>

            {/* Add form */}
            <form onSubmit={add} className="space-y-2">
                <div className="flex gap-2">
                    <input
                        data-testid="shopping-input"
                        className="brut-input"
                        placeholder={t("shop.add_placeholder")}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onFocus={() => setExpanded(true)}
                    />
                    <button data-testid="add-shopping-btn" type="submit" className="brut-btn">
                        <Plus className="w-4 h-4" /> {t("shop.add_btn")}
                    </button>
                </div>
                <AnimatePresence>
                    {expanded && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                        >
                            <div className="grid grid-cols-2 gap-2 pt-1">
                                <select
                                    data-testid="shopping-store-select"
                                    className="brut-input !py-2 !text-sm"
                                    value={store}
                                    onChange={(e) => {
                                        if (e.target.value === "__add__") {
                                            setShowAddStore(true);
                                        } else {
                                            setStore(e.target.value);
                                        }
                                    }}
                                >
                                    <option value="">{t("shop.any_store")}</option>
                                    {stores.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                    <option value="__add__">{t("shop.add_store_option")}</option>
                                </select>
                                <select
                                    data-testid="shopping-category-select"
                                    className="brut-input !py-2 !text-sm"
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                >
                                    <option value="">{t("shop.any_category")}</option>
                                    {CATEGORIES.map((c) => (
                                        <option key={c} value={c}>{t(`shop.cat.${c}`)}</option>
                                    ))}
                                </select>
                            </div>
                            <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                                <input
                                    data-testid="shopping-personal-checkbox"
                                    type="checkbox"
                                    checked={personal}
                                    onChange={(e) => setPersonal(e.target.checked)}
                                    className="w-4 h-4 rounded accent-[#2F6B43]"
                                />
                                <span className="text-xs font-semibold text-[#0d2a1b]">
                                    {t("shop.personal_label")}
                                </span>
                            </label>
                        </motion.div>
                    )}
                </AnimatePresence>
            </form>

            {/* Add Store inline form */}
            {showAddStore && (
                <form onSubmit={submitNewStore} className="brut-card p-3 flex gap-2 items-center">
                    <ShopFourTiles className="w-4 h-4 text-[#2F6B43]" />
                    <input
                        data-testid="new-store-input"
                        className="brut-input !py-2 !text-sm flex-1"
                        placeholder={t("shop.store_name_placeholder")}
                        value={newStoreName}
                        onChange={(e) => setNewStoreName(e.target.value)}
                        autoFocus
                    />
                    <button type="submit" data-testid="add-store-btn" className="brut-btn text-xs">
                        <Plus className="w-3.5 h-3.5" /> {t("shop.add_btn")}
                    </button>
                    <button type="button" onClick={() => setShowAddStore(false)} className="brut-btn brut-btn-secondary text-xs">
                        <Xmark className="w-3.5 h-3.5" />
                    </button>
                </form>
            )}

            {/* Sort pills */}
            <div className="flex gap-2 overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6 scrollbar-hide">
                <span className="text-[10px] font-bold text-[#5b6d63] uppercase tracking-widest self-center shrink-0">{t("shop.sort_label_short")}</span>
                {SORTS.map((s) => {
                    const active = sort === s.id;
                    return (
                        <button
                            key={s.id}
                            data-testid={`sort-${s.id}-btn`}
                            onClick={() => setSort(s.id)}
                            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${active ? "bg-[#2F6B43] text-white" : "bg-white border border-[#0d2a1b]/8 hover:bg-[#faf5ea]"}`}
                        >
                            {s.label}
                        </button>
                    );
                })}
            </div>

            {/* Show (tag filter) pills */}
            <div className="flex gap-2 overflow-x-auto -mx-5 sm:-mx-6 px-5 sm:px-6 scrollbar-hide" data-testid="shop-show-row">
                <span className="text-[10px] font-bold text-[#5b6d63] uppercase tracking-widest self-center shrink-0">{t("shop.show_label")}</span>
                {TAG_IDS.map((tagId) => {
                    const active = !!show[tagId];
                    return (
                        <button
                            key={tagId}
                            data-testid={`show-${tagId}-btn`}
                            onClick={() => setShow((s) => {
                                const next = { ...s, [tagId]: !s[tagId] };
                                // Groceries and Home Improvement are mutually exclusive views.
                                // Turning one ON turns the other OFF; turning the active one OFF turns the other ON.
                                if (tagId === "home") {
                                    next.groceries = !next.home;
                                } else if (tagId === "groceries") {
                                    next.home = !next.groceries;
                                }
                                return next;
                            })}
                            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${active ? "bg-[#2F6B43] text-white" : "bg-white border border-[#0d2a1b]/8 hover:bg-[#faf5ea] text-[#5b6d63] line-through"}`}
                            aria-pressed={active}
                        >
                            {t(`shop.tag.${tagId}`)}
                        </button>
                    );
                })}
            </div>

            {/* Missing items */}
            <section className="brut-card p-5">
                <AnimatePresence>
                    {!autoCheck && !fixDismissed && inPantryPending.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: -6, height: 0 }}
                            animate={{ opacity: 1, y: 0, height: "auto" }}
                            exit={{ opacity: 0, y: -6, height: 0 }}
                            className="overflow-hidden"
                            data-testid="fix-banner"
                        >
                            <div className="mb-3 rounded-xl border-2 border-[#2F6B43] bg-[#e8f1e4] p-3 flex items-center justify-between gap-3 flex-wrap">
                                <div className="text-sm font-semibold text-[#0d2a1b] min-w-0 flex-1">
                                    ✨ {inPantryPending.length === 1
                                        ? t("shop.fix_banner_one")
                                        : t("shop.fix_banner_other", { n: inPantryPending.length })}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    <button
                                        type="button"
                                        onClick={runBulkCheck}
                                        disabled={fixBusy}
                                        className="brut-btn !py-1.5 !px-3 text-[11px]"
                                        data-testid="fix-banner-check-all"
                                    >
                                        <Check className="w-3.5 h-3.5" /> {t("shop.fix_check_all")}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFixDismissed(true)}
                                        className="brut-btn brut-btn-secondary !py-1.5 !px-3 text-[11px]"
                                        data-testid="fix-banner-dismiss"
                                    >
                                        {t("shop.fix_dismiss")}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#5b6d63]">
                        {t("shop.missing_count", { n: pending.length })}
                    </h2>
                </div>
                {pending.length === 0 ? (
                    <div className="text-sm text-[#5b6d63] py-2">{t("shop.empty_fridge")}</div>
                ) : sort === "category" ? (
                    <CategoryGroupedList
                        pending={pending}
                        members={members}
                        stores={stores}
                        editingId={editingId}
                        setEditingId={setEditingId}
                        saveEdit={saveEdit}
                        toggle={toggle}
                        tagLater={tagLater}
                        remove={remove}
                        setShowAddStore={setShowAddStore}
                        t={t}
                    />
                ) : (
                    <ul>
                        <AnimatePresence>
                            {pending.map((it) => (
                                <ShoppingRow
                                    key={it.id}
                                    it={it}
                                    members={members}
                                    stores={stores}
                                    isEditing={editingId === it.id}
                                    onEdit={() => setEditingId(it.id)}
                                    onCancelEdit={() => setEditingId(null)}
                                    onSaveEdit={(updates) => saveEdit(it.id, updates)}
                                    onToggle={() => toggle(it)}
                                    onTagLater={() => tagLater(it)}
                                    onRemove={() => remove(it)}
                                    onAddStore={() => setShowAddStore(true)}
                                />
                            ))}
                        </AnimatePresence>
                    </ul>
                )}
            </section>

            {/* Bought */}
            <section className="brut-card p-5">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <h2 className="text-[11px] uppercase tracking-[0.14em] font-bold text-[#5b6d63]">{t("shop.bought")}</h2>
                    <div className="flex gap-1.5 flex-wrap">
                        {BOUGHT_FILTERS.map((f) => {
                            const active = boughtFilter === f.id;
                            const count = boughtStats[f.id] ?? 0;
                            return (
                                <button
                                    key={f.id}
                                    data-testid={`bought-${f.id}-btn`}
                                    onClick={() => setBoughtFilter(f.id)}
                                    className={`px-2.5 py-1 rounded-full text-[10.5px] font-bold transition-colors ${active ? "bg-[#0d2a1b] text-white" : "bg-[#faf5ea] hover:bg-[#e8f1e4]"}`}
                                >
                                    {f.label} · {count}
                                </button>
                            );
                        })}
                    </div>
                </div>
                {boughtFilter === "avg_per_week" ? (
                    <div className="text-center py-6">
                        <div className="font-display text-5xl font-bold">{boughtStats.avg_per_week}</div>
                        <div className="text-xs font-semibold text-[#5b6d63] mt-1 uppercase tracking-widest">{t("shop.avg_per_week_label")}</div>
                    </div>
                ) : boughtList.length === 0 ? (
                    <div className="text-sm text-[#5b6d63]">{t("shop.no_period")}</div>
                ) : (
                    <ul>
                        {boughtList.map((it) => {
                            const m = memberOf(it.created_by_member_id);
                            return (
                                <li key={it.id} className="flex items-center gap-3 py-2 border-b border-[#0d2a1b]/5 last:border-0">
                                    <button
                                        data-testid={`toggle-item-${it.id}-btn`}
                                        onClick={() => toggle(it)}
                                        className="w-5 h-5 rounded-full bg-[#2F6B43] text-white flex items-center justify-center shrink-0"
                                    >
                                        <Check className="w-3 h-3" strokeWidth={3} />
                                    </button>
                                    <div className="flex-1 text-sm line-through text-[#5b6d63]">
                                        {it.name}
                                        {it.store && <span className="ml-2 text-[10px] text-[#5b6d63] uppercase tracking-widest">@ {it.store}</span>}
                                    </div>
                                    {m && (
                                        <div
                                            className="avatar !w-5 !h-5 !text-[9px] !border-white"
                                            style={{ background: avatarColor(m.name || m.id) }}
                                            title={firstName(m.name)}
                                        >
                                            {initials(m.name)}
                                        </div>
                                    )}
                                    <button
                                        onClick={() => remove(it)}
                                        className="w-7 h-7 rounded-full hover:bg-[#fbe0dc] text-zinc-400 flex items-center justify-center"
                                    >
                                        <Trash className="w-3.5 h-3.5" />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>
        </div>
    );
}

function ShoppingRow({ it, members, stores, isEditing, onEdit, onCancelEdit, onSaveEdit, onToggle, onTagLater, onRemove, onAddStore }) {
    const { t } = useT();
    const memberOf = (mid) => members?.find((m) => m.id === mid);
    if (isEditing) {
        return (
            <motion.li
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-2.5 border-b border-[#0d2a1b]/5 last:border-0"
            >
                <EditShoppingForm it={it} stores={stores} onCancel={onCancelEdit} onSave={onSaveEdit} onAddStore={onAddStore} />
            </motion.li>
        );
    }
    const owner = memberOf(it.created_by_member_id);
    return (
        <motion.li
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className={`flex items-center gap-3 py-2.5 border-b border-[#0d2a1b]/5 last:border-0 ${it.tagged_later ? "opacity-60" : ""}`}
        >
            <button
                data-testid={`toggle-item-${it.id}-btn`}
                onClick={onToggle}
                className="w-5 h-5 rounded-full border-[1.5px] border-[#2F6B43]/40 hover:bg-[#e8f1e4] hover:border-[#2F6B43] transition-colors shrink-0"
                aria-label="check"
            />
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap">
                    {it.name}
                    {it.personal && (
                        <span className="text-[9px] font-bold uppercase tracking-widest bg-[#ece4f5] text-[#5d3b94] px-1.5 py-0.5 rounded-full">
                            {t("shop.badge.personal")}
                        </span>
                    )}
                    {it.tagged_later && (
                        <span className="text-[9px] font-bold uppercase tracking-widest bg-[#fbeecb] text-[#7a5c1a] px-1.5 py-0.5 rounded-full">
                            {t("shop.badge.later")}
                        </span>
                    )}
                    {it.priority && (
                        <span className="text-[9px] font-bold uppercase tracking-widest bg-[#fbd9d4] text-[#7a2a2a] px-1.5 py-0.5 rounded-full">
                            ★ {t("shop.badge.priority")}
                        </span>
                    )}
                    {it.home_improvement && (
                        <span className="text-[9px] font-bold uppercase tracking-widest bg-[#cfe6dc] text-[#1f4d2f] px-1.5 py-0.5 rounded-full">
                            🔨 {t("shop.badge.home")}
                        </span>
                    )}
                    {it.in_pantry && (
                        <span
                            className="text-[9px] font-bold uppercase tracking-widest bg-[#e6f1d8] text-[#3a5520] px-1.5 py-0.5 rounded-full"
                            title={`${it.in_pantry.quantity || ""} ${it.in_pantry.unit || ""}`.trim()}
                            data-testid={`in-pantry-${it.id}`}
                        >
                            🥫 {t("shop.in_pantry_at", { location: it.in_pantry.location_name || t("shop.in_pantry") })}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 text-[10.5px] text-[#5b6d63] mt-0.5">
                    {it.store && <span className="font-semibold">@ {it.store}</span>}
                    {it.category && <span>· {t(`shop.cat.${it.category}`)}</span>}
                    {owner && <span className="flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: avatarColor(owner.name || owner.id) }} />
                        {firstName(owner.name)}
                    </span>}
                </div>
            </div>
            <button
                data-testid={`later-${it.id}-btn`}
                onClick={onTagLater}
                className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors ${it.tagged_later ? "bg-[#fbeecb]" : "hover:bg-[#fbeecb]"}`}
                title={t("shop.tag_later")}
                aria-label="later"
            >
                <Clock className="w-3.5 h-3.5" />
            </button>
            <button
                data-testid={`edit-${it.id}-btn`}
                onClick={onEdit}
                className="w-7 h-7 rounded-full hover:bg-[#e8f1e4] flex items-center justify-center shrink-0"
                aria-label="edit"
            >
                <EditPencil className="w-3.5 h-3.5" />
            </button>
            <button
                data-testid={`delete-item-${it.id}-btn`}
                onClick={onRemove}
                className="w-7 h-7 rounded-full hover:bg-[#fbe0dc] text-zinc-400 hover:text-[#A53F3F] flex items-center justify-center shrink-0"
                aria-label="delete"
            >
                <Trash className="w-3.5 h-3.5" />
            </button>
        </motion.li>
    );
}

function EditShoppingForm({ it, stores, onCancel, onSave, onAddStore }) {
    const { t } = useT();
    const [name, setName] = useState(it.name || "");
    const [store, setStore] = useState(it.store || "");
    const [category, setCategory] = useState(it.category || "");
    const [tags, setTags] = useState({
        personal: !!it.personal,
        later: !!it.tagged_later,
        priority: !!it.priority,
        home: !!it.home_improvement,
    });

    const submit = (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        onSave({
            name: name.trim(),
            store: store || null,
            category: category || null,
            personal: tags.personal,
            tagged_later: tags.later,
            priority: tags.priority,
            home_improvement: tags.home,
        });
    };

    return (
        <form onSubmit={submit} className="space-y-2">
            <input
                className="brut-input !py-2 !text-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                data-testid={`edit-name-${it.id}`}
            />
            <div className="grid grid-cols-2 gap-2">
                <select
                    className="brut-input !py-2 !text-sm"
                    value={store}
                    onChange={(e) => {
                        if (e.target.value === "__add__") {
                            onAddStore?.();
                        } else {
                            setStore(e.target.value);
                        }
                    }}
                    data-testid={`edit-store-${it.id}`}
                >
                    <option value="">{t("shop.any_store")}</option>
                    {stores.map((s) => <option key={s} value={s}>{s}</option>)}
                    <option value="__add__">{t("shop.add_store_option")}</option>
                </select>
                <select
                    className="brut-input !py-2 !text-sm"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    data-testid={`edit-cat-${it.id}`}
                >
                    <option value="">{t("shop.any_category")}</option>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{t(`shop.cat.${c}`)}</option>)}
                </select>
            </div>
            {/* Multi-select tag chips */}
            <div className="flex gap-1.5 flex-wrap">
                {TAG_IDS.filter((id) => id !== "groceries").map((id) => {
                    const active = !!tags[id];
                    return (
                        <button
                            type="button"
                            key={id}
                            data-testid={`edit-tag-${id}-${it.id}`}
                            onClick={() => setTags((p) => ({ ...p, [id]: !p[id] }))}
                            aria-pressed={active}
                            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-[#0d2a1b] text-white border-[#0d2a1b]" : "bg-white text-[#5b6d63] border-[#0d2a1b]/15 hover:bg-[#faf5ea]"}`}
                        >
                            {t(`shop.tag.${id}`)}
                        </button>
                    );
                })}
            </div>
            <div className="flex gap-2">
                <button type="submit" className="brut-btn text-xs flex-1 justify-center" data-testid={`save-edit-${it.id}-btn`}>
                    <Check className="w-3.5 h-3.5" /> {t("common.save")}
                </button>
                <button type="button" onClick={onCancel} className="brut-btn brut-btn-secondary text-xs">
                    <Xmark className="w-3.5 h-3.5" />
                </button>
            </div>
        </form>
    );
}


const CATEGORY_ORDER = ["Produce", "Dairy", "Meat", "Pantry", "Frozen", "Bakery", "Beverages", "Other"];

function CategoryGroupedList({
    pending, members, stores, editingId, setEditingId, saveEdit, toggle, tagLater, remove, setShowAddStore, t,
}) {
    const groups = {};
    for (const it of pending) {
        const key = it.category || "__none__";
        if (!groups[key]) groups[key] = [];
        groups[key].push(it);
    }
    const orderedKeys = [
        ...CATEGORY_ORDER.filter((c) => groups[c]?.length),
        ...(groups["__none__"]?.length ? ["__none__"] : []),
    ];
    return (
        <div data-testid="shopping-grouped" className="space-y-4">
            {orderedKeys.map((key) => {
                const items = groups[key];
                const label = key === "__none__" ? t("shop.uncategorised") : t(`shop.cat.${key}`);
                return (
                    <div key={key} data-testid={`shopping-group-${key}`}>
                        <div className="flex items-center gap-2 mb-1.5">
                            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#2F6B43]">
                                {label}
                            </div>
                            <div className="flex-1 h-px bg-[#0d2a1b]/10" />
                            <div className="text-[10px] font-bold text-[#5b6d63] tabular-nums">
                                {items.length}
                            </div>
                        </div>
                        <ul>
                            <AnimatePresence>
                                {items.map((it) => (
                                    <ShoppingRow
                                        key={it.id}
                                        it={it}
                                        members={members}
                                        stores={stores}
                                        isEditing={editingId === it.id}
                                        onEdit={() => setEditingId(it.id)}
                                        onCancelEdit={() => setEditingId(null)}
                                        onSaveEdit={(updates) => saveEdit(it.id, updates)}
                                        onToggle={() => toggle(it)}
                                        onTagLater={() => tagLater(it)}
                                        onRemove={() => remove(it)}
                                        onAddStore={() => setShowAddStore(true)}
                                    />
                                ))}
                            </AnimatePresence>
                        </ul>
                    </div>
                );
            })}
        </div>
    );
}
